import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { RoleShell } from '../components/RoleShell';
import { TrendingUp, Users, IndianRupee, Clock, CheckCircle2, AlertCircle, CalendarDays, Banknote, Smartphone, CreditCard, Tag, Receipt as ReceiptIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KpiCard = ({ icon: Icon, label, value, accent = 'slate', delta }) => {
  const accents = {
    slate:  { color: 'var(--color-slate)', bg: 'rgba(61, 90, 128, 0.08)' },
    copper: { color: '#B5612E',            bg: 'rgba(212, 120, 74, 0.08)' },
    green:  { color: '#16A34A',            bg: 'rgba(22, 163, 74, 0.08)' },
    amber:  { color: '#F59E0B',            bg: 'rgba(245, 158, 11, 0.08)' },
  };
  const a = accents[accent] || accents.slate;
  return (
    <div className="glass-primary rounded-[var(--radius-xl)] p-5 flex flex-col gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: a.bg }}>
          <Icon size={18} strokeWidth={1.75} style={{ color: a.color }} />
        </div>
        {delta && (
          <span className="font-['Sora'] text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: a.bg, color: a.color }}>
            {delta}
          </span>
        )}
      </div>
      <div>
        <div className="font-['Inter'] text-[13px] uppercase tracking-wider" style={{ color: '#78716C' }}>{label}</div>
        <div className="font-['DM_Mono'] text-[34px] font-semibold mt-1.5" style={{ color: '#1C1917' }}>{value}</div>
      </div>
    </div>
  );
};

const RevenueChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.revenue), 100);
  const width = 700;
  const height = 200;
  const stepX = width / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => `${i * stepX},${height - (d.revenue / max) * (height - 20) - 10}`).join(' ');

  return (
    <div className="glass-primary rounded-[var(--radius-xl)] p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-['Sora'] text-base font-semibold" style={{ color: '#1C1917' }}>Today's Revenue</h3>
        <span className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>by hour</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height + 30}`} width="100%" height="240" data-testid="revenue-chart">
        <defs>
          <linearGradient id="copperFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#D4784A" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#D4784A" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id="copperStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#B5612E" />
            <stop offset="100%" stopColor="#D4784A" />
          </linearGradient>
        </defs>
        <polygon
          points={`0,${height} ${points} ${width},${height}`}
          fill="url(#copperFill)"
        />
        <polyline points={points} fill="none" stroke="url(#copperStroke)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => (
          <g key={d.hour}>
            <circle cx={i * stepX} cy={height - (d.revenue / max) * (height - 20) - 10} r="3.5" fill="#B5612E" />
            <text x={i * stepX} y={height + 22} textAnchor="middle" fontFamily="DM Mono" fontSize="10" fill="#78716C">{d.hour}:00</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export const OwnerView = () => {
  const { token, user } = useAuth();
  const { settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats/today`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(res.data);
        setLoading(false);
      } catch (e) {
        console.error('Failed to fetch stats', e);
        setLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, [token]);

  const totals = stats?.totals || {};
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <RoleShell titlePrefix="Owner Dashboard" userName={user?.name || 'Dr. R. Sharma'}>
      <div className="overflow-y-auto" style={{ background: 'transparent', padding: '32px 40px', minHeight: 'calc(100vh - 68px)' }} data-testid="owner-view">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-['Sora'] text-[32px] font-bold" style={{ color: '#1C1917' }}>
                Good morning, {settings.doctor_name.replace(/^Dr\.?\s+/i, '').split(' ')[0]}
              </h1>
              <p className="font-['Inter'] text-[15px] mt-2" style={{ color: '#78716C' }}>
                <CalendarDays size={14} strokeWidth={1.75} className="inline mr-1.5" />
                {today} · {settings.clinic_name}
              </p>
            </div>
          </div>

          {loading && !stats && (
            <div className="glass-primary rounded-[var(--radius-xl)] p-10 text-center font-['Inter'] text-sm" style={{ color: '#78716C' }}>
              Loading dashboard…
            </div>
          )}

          {stats && (
            <>
              {/* Top KPI row */}
              <div className="grid grid-cols-4 gap-4 mb-5">
                <KpiCard icon={IndianRupee} label="Revenue Today" value={`₹${stats.revenue_today || 0}`} accent="copper" />
                <KpiCard icon={Users}        label="Patients Today" value={totals.patients || 0} accent="slate" />
                <KpiCard icon={CheckCircle2} label="Completed"     value={totals.completed || 0} accent="green" />
                <KpiCard icon={Clock}        label="Avg Wait"      value={`${stats.avg_wait_minutes || 0}m`} accent="amber" />
              </div>

              {/* Payment breakdown — the section the owner actually cares about */}
              <PaymentBreakdown stats={stats} />

              {/* Operational row */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <KpiCard icon={Clock}       label="In Queue"        value={totals.in_queue || 0} accent="slate" />
                <KpiCard icon={Users}       label="Waiting"         value={totals.waiting || 0} accent="slate" />
                <KpiCard icon={TrendingUp}  label="In Consultation" value={totals.in_consultation || 0} accent="copper" />
                <KpiCard icon={AlertCircle} label="No Shows"        value={totals.no_show || 0} accent="amber" />
              </div>

              <RevenueChart data={stats.hourly_revenue} />

              {/* Lower row: outstanding + capacity */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="glass-primary rounded-[var(--radius-xl)] p-6">
                  <h3 className="font-['Sora'] text-base font-semibold mb-3" style={{ color: '#1C1917' }}>Outstanding</h3>
                  <div className="flex items-baseline gap-3">
                    <div className="font-['DM_Mono'] text-[36px] font-semibold" style={{ color: '#F59E0B' }}>
                      ₹{stats.outstanding_total || 0}
                    </div>
                    <span className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>
                      across {totals.payment_pending || 0} patient{(totals.payment_pending || 0) === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="font-['Inter'] text-[13px] mt-2" style={{ color: '#78716C' }}>Currently awaiting collection at billing</p>
                </div>

                <div className="glass-primary rounded-[var(--radius-xl)] p-6">
                  <h3 className="font-['Sora'] text-base font-semibold mb-3" style={{ color: '#1C1917' }}>Capacity</h3>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(28,25,23,0.06)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(((totals.in_consultation || 0) + (totals.waiting || 0) + (totals.in_queue || 0)) * 10, 100)}%`,
                          background: 'linear-gradient(90deg, var(--color-slate), #B5612E)'
                        }}
                      />
                    </div>
                    <span className="font-['DM_Mono'] text-sm font-medium" style={{ color: '#44403C' }}>
                      {(totals.in_consultation || 0) + (totals.waiting || 0) + (totals.in_queue || 0)} active
                    </span>
                  </div>
                  <p className="font-['Inter'] text-[13px] mt-2" style={{ color: '#78716C' }}>Live patients across queue + consultation</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </RoleShell>
  );
};

export default OwnerView;

const PaymentBreakdown = ({ stats }) => {
  const breakdown = stats.payment_breakdown || { cash: 0, upi: 0, card: 0, other: 0 };
  const counts = stats.payment_breakdown_count || { cash: 0, upi: 0, card: 0, other: 0 };
  const total = breakdown.cash + breakdown.upi + breakdown.card + breakdown.other;

  const items = [
    { key: 'cash', label: 'Cash',     icon: Banknote,   color: '#16A34A', light: '#ECFDF5', value: breakdown.cash, count: counts.cash },
    { key: 'upi',  label: 'UPI',      icon: Smartphone, color: '#3D5A80', light: '#EFF6FF', value: breakdown.upi,  count: counts.upi },
    { key: 'card', label: 'Card',     icon: CreditCard, color: '#B5612E', light: '#FFF7ED', value: breakdown.card, count: counts.card },
    { key: 'discount', label: 'Discount given', icon: Tag, color: '#7C3AED', light: '#F5F3FF', value: stats.total_discount || 0, count: null, isDiscount: true },
  ];

  return (
    <div className="glass-primary rounded-[var(--radius-xl)] p-6 mb-5" data-testid="payment-breakdown">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ReceiptIcon size={18} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
          <h3 className="font-['Sora'] text-[17px] font-semibold" style={{ color: '#1C1917' }}>
            Today's collection · ₹{total}
          </h3>
        </div>
        <span className="font-['Inter'] text-[13px]" style={{ color: '#78716C' }}>
          Avg ticket · ₹{stats.avg_ticket || 0}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {items.map(item => {
          const Icon = item.icon;
          const pct = total > 0 && !item.isDiscount ? (item.value / total) * 100 : 0;
          return (
            <div
              key={item.key}
              className="rounded-[16px] p-4"
              style={{
                background: item.light,
                border: `1px solid ${item.color}22`,
                boxShadow: '0 6px 14px -4px rgba(28, 25, 23, 0.06)',
              }}
              data-testid={`payment-${item.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${item.color}1A` }}>
                  <Icon size={17} strokeWidth={1.75} style={{ color: item.color }} />
                </div>
                {item.count !== null && (
                  <span className="font-['Sora'] text-[11px] font-semibold" style={{ color: item.color, opacity: 0.8 }}>
                    {item.count} txn
                  </span>
                )}
                {item.isDiscount && (
                  <span className="font-['Sora'] text-[11px] font-semibold" style={{ color: item.color, opacity: 0.8 }}>
                    waived
                  </span>
                )}
              </div>
              <div className="font-['Inter'] text-[12px] uppercase tracking-wider" style={{ color: '#78716C' }}>{item.label}</div>
              <div className="font-['DM_Mono'] text-[28px] font-semibold mt-1" style={{ color: item.color }}>
                ₹{item.value}
              </div>
              {!item.isDiscount && (
                <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: `${item.color}1A` }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: item.color,
                      transition: 'width 600ms ease',
                    }}
                  />
                </div>
              )}
              {!item.isDiscount && (
                <div className="font-['Sora'] text-[11px] font-semibold mt-1.5" style={{ color: item.color, opacity: 0.7 }}>
                  {Math.round(pct)}% of total
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

